import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import type { SiteListFilters as FiltersType } from "./useSiteList";
import { EMPTY_FILTERS } from "./useSiteList";

interface SiteListFiltersProps {
  onChange: (filters: FiltersType) => void;
}

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Все" },
  { value: "valid", label: "Valid" },
  { value: "invalid", label: "Invalid" },
  { value: "unchecked", label: "Unchecked" },
];

const HOMEPAGE_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Все" },
  { value: "valid", label: "Valid" },
  { value: "invalid", label: "Invalid" },
  { value: "unchecked", label: "Unchecked" },
];

const SITE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Любые" },
  { value: "domain", label: "Домен" },
  { value: "subdomain", label: "Субдомен" },
];

const ACCESS_VALIDITY_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Любые" },
  { value: "valid", label: "Только с валидным доступом" },
  { value: "article", label: "Валидные для article" },
  { value: "homepage", label: "Валидные для homepage" },
  { value: "sitewide", label: "Валидные для sitewide" },
  { value: "link_removal", label: "Валидные для удаления (link-removal)" },
  { value: "invalid", label: "Без валидного доступа" },
];

const PLUGIN_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Любые" },
  { value: "installed", label: "Установлен" },
  { value: "valid", label: "Валидный" },
  { value: "invalid", label: "Невалидный" },
];

const DEBOUNCE_MS = 400;

export function SiteListFilters(props: SiteListFiltersProps) {
  const { onChange } = props;
  const [local, setLocal] = useState<FiltersType>(EMPTY_FILTERS);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const updateField = <K extends keyof FiltersType>(
    field: K,
    value: FiltersType[K],
  ) => {
    setLocal((previous) => ({ ...previous, [field]: value }));
  };

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      onChangeRef.current(local);
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [local]);

  const handleCheckboxChange = (checked: boolean) => {
    const next: FiltersType = { ...local, withoutParams: checked };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleSiteTypeChange = (value: string) => {
    const siteType = value === "" ? undefined : (value as NonNullable<FiltersType["siteType"]>);
    const next: FiltersType = { ...local, siteType };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleAccessValidityChange = (value: string) => {
    const accessValidity =
      value === "" ? undefined : (value as NonNullable<FiltersType["accessValidity"]>);
    const next: FiltersType = { ...local, accessValidity };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleArticleXmlRpcStatusChange = (value: string) => {
    const next: FiltersType = {
      ...local,
      articleXmlRpcStatus:
        value === "" ? undefined : (value as NonNullable<FiltersType["articleXmlRpcStatus"]>),
    };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleArticleApiStatusChange = (value: string) => {
    const next: FiltersType = {
      ...local,
      articleApiStatus:
        value === "" ? undefined : (value as NonNullable<FiltersType["articleApiStatus"]>),
    };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleArticleWpAdminStatusChange = (value: string) => {
    const next: FiltersType = {
      ...local,
      articleWpAdminStatus:
        value === "" ? undefined : (value as NonNullable<FiltersType["articleWpAdminStatus"]>),
    };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleHomepageApiStatusChange = (value: string) => {
    const next: FiltersType = {
      ...local,
      homepageApiStatus:
        value === "" ? undefined : (value as NonNullable<FiltersType["homepageApiStatus"]>),
    };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleHomepageWpAdminStatusChange = (value: string) => {
    const next: FiltersType = {
      ...local,
      homepageWpAdminStatus:
        value === "" ? undefined : (value as NonNullable<FiltersType["homepageWpAdminStatus"]>),
    };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleLinkRemovalApiStatusChange = (value: string) => {
    const next: FiltersType = {
      ...local,
      linkRemovalApiStatus:
        value === "" ? undefined : (value as NonNullable<FiltersType["linkRemovalApiStatus"]>),
    };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleLinkRemovalXmlRpcStatusChange = (value: string) => {
    const next: FiltersType = {
      ...local,
      linkRemovalXmlRpcStatus:
        value === "" ? undefined : (value as NonNullable<FiltersType["linkRemovalXmlRpcStatus"]>),
    };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleLinkRemovalWpAdminStatusChange = (value: string) => {
    const next: FiltersType = {
      ...local,
      linkRemovalWpAdminStatus:
        value === "" ? undefined : (value as NonNullable<FiltersType["linkRemovalWpAdminStatus"]>),
    };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handlePluginArticleStatusChange = (value: string) => {
    const pluginArticleStatusFilter =
      value === "" ? undefined : (value as NonNullable<FiltersType["pluginArticleStatusFilter"]>);
    const next: FiltersType = { ...local, pluginArticleStatusFilter };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handlePluginStatusChange = (value: string) => {
    const pluginHomepageStatusFilter =
      value === "" ? undefined : (value as NonNullable<FiltersType["pluginHomepageStatusFilter"]>);
    const next: FiltersType = { ...local, pluginHomepageStatusFilter };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handlePluginSitewideStatusChange = (value: string) => {
    const pluginSitewideStatusFilter =
      value === "" ? undefined : (value as NonNullable<FiltersType["pluginSitewideStatusFilter"]>);
    const next: FiltersType = { ...local, pluginSitewideStatusFilter };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handlePluginLinkRemovalStatusChange = (value: string) => {
    const pluginLinkRemovalStatusFilter =
      value === "" ? undefined : (value as NonNullable<FiltersType["pluginLinkRemovalStatusFilter"]>);
    const next: FiltersType = { ...local, pluginLinkRemovalStatusFilter };
    setLocal(next);
    onChangeRef.current(next);
  };

  const handleReset = () => {
    setLocal(EMPTY_FILTERS);
    onChangeRef.current(EMPTY_FILTERS);
  };

  const hasBasicActiveFilters =
    local.region !== "" ||
    local.siteType !== undefined ||
    local.accessValidity !== undefined ||
    local.seller !== "";

  const hasAdvancedActiveFilters =
    local.articleXmlRpcStatus !== undefined ||
    local.articleApiStatus !== undefined ||
    local.articleWpAdminStatus !== undefined ||
    local.homepageApiStatus !== undefined ||
    local.homepageWpAdminStatus !== undefined ||
    local.linkRemovalApiStatus !== undefined ||
    local.linkRemovalXmlRpcStatus !== undefined ||
    local.linkRemovalWpAdminStatus !== undefined ||
    local.pluginArticleStatusFilter !== undefined ||
    local.pluginHomepageStatusFilter !== undefined ||
    local.pluginSitewideStatusFilter !== undefined ||
    local.pluginLinkRemovalStatusFilter !== undefined ||
    local.dateFrom !== "" ||
    local.dateTo !== "" ||
    local.minArticlePostsCount !== "" ||
    local.minHomepagePostsCount !== "" ||
    local.maxArticleAttemptErrors !== "" ||
    local.maxHomepageAttemptErrors !== "" ||
    local.withoutParams;

  const hasActiveFilters = hasBasicActiveFilters || hasAdvancedActiveFilters;

  const accessValiditySelectValue = local.accessValidity ?? "";
  const siteTypeSelectValue = local.siteType ?? "";

  return (
    <Box>
      <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
        <Box flex="1 1 160px" maxWidth={200}>
          <TextField fullWidth
            size="small"
            label="Поиск по URL"
            value={local.search}
            onChange={(event) => updateField("search", event.target.value)}
          />
        </Box>
        <Box flex="1 1 100px" maxWidth={200}>
          <TextField fullWidth
            size="small"
            label="Регион"
            value={local.region}
            onChange={(event) => updateField("region", event.target.value)}
          />
        </Box>
        <Box flex="1 1 140px" maxWidth={180}>
          <FormControl fullWidth size="small">
            <InputLabel>Тип сайта</InputLabel>
            <Select
              value={siteTypeSelectValue}
              label="Тип сайта"
              onChange={(event) => handleSiteTypeChange(event.target.value)}
            >
              {SITE_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box flex="1 1 220px" maxWidth={260}>
          <FormControl fullWidth size="small">
            <InputLabel>Валидность доступов</InputLabel>
            <Select
              value={accessValiditySelectValue}
              label="Валидность доступов"
              onChange={(event) => handleAccessValidityChange(event.target.value)}
            >
              {ACCESS_VALIDITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box flex="1 1 100px" maxWidth={200}>
          <TextField fullWidth
            size="small"
            label="Селлер"
            value={local.seller}
            onChange={(event) => updateField("seller", event.target.value)}
          />
        </Box>
        {hasActiveFilters && (
          <Button size="small" onClick={handleReset}>
            Сбросить фильтры
          </Button>
        )}
        <Button
          size="small"
          onClick={() => setAdvancedOpen((value) => !value)}
          endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          color={hasAdvancedActiveFilters ? "primary" : "inherit"}
        >
          Дополнительные фильтры
          {hasAdvancedActiveFilters && " •"}
        </Button>
      </Box>
      <Collapse in={advancedOpen} timeout="auto" unmountOnExit>
        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mt={1}>
          <Box flex="1 1 140px" maxWidth={180}>
            <FormControl fullWidth size="small">
              <InputLabel>Article XML-RPC</InputLabel>
              <Select
                value={local.articleXmlRpcStatus ?? ""}
                label="Article XML-RPC"
                onChange={(event) => handleArticleXmlRpcStatusChange(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box flex="1 1 140px" maxWidth={180}>
            <FormControl fullWidth size="small">
              <InputLabel>Article API</InputLabel>
              <Select
                value={local.articleApiStatus ?? ""}
                label="Article API"
                onChange={(event) => handleArticleApiStatusChange(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box flex="1 1 140px" maxWidth={180}>
            <FormControl fullWidth size="small">
              <InputLabel>Article wp-admin</InputLabel>
              <Select
                value={local.articleWpAdminStatus ?? ""}
                label="Article wp-admin"
                onChange={(event) => handleArticleWpAdminStatusChange(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box flex="1 1 140px" maxWidth={180}>
            <FormControl fullWidth size="small">
              <InputLabel>Homepage API</InputLabel>
              <Select
                value={local.homepageApiStatus ?? ""}
                label="Homepage API"
                onChange={(event) => handleHomepageApiStatusChange(event.target.value)}
              >
                {HOMEPAGE_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box flex="1 1 140px" maxWidth={180}>
            <FormControl fullWidth size="small">
              <InputLabel>Homepage wp-admin</InputLabel>
              <Select
                value={local.homepageWpAdminStatus ?? ""}
                label="Homepage wp-admin"
                onChange={(event) => handleHomepageWpAdminStatusChange(event.target.value)}
              >
                {HOMEPAGE_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box flex="1 1 140px" maxWidth={180}>
            <FormControl fullWidth size="small">
              <InputLabel>Link-removal API</InputLabel>
              <Select
                value={local.linkRemovalApiStatus ?? ""}
                label="Link-removal API"
                onChange={(event) => handleLinkRemovalApiStatusChange(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box flex="1 1 140px" maxWidth={180}>
            <FormControl fullWidth size="small">
              <InputLabel>Link-removal XML-RPC</InputLabel>
              <Select
                value={local.linkRemovalXmlRpcStatus ?? ""}
                label="Link-removal XML-RPC"
                onChange={(event) => handleLinkRemovalXmlRpcStatusChange(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box flex="1 1 140px" maxWidth={180}>
            <FormControl fullWidth size="small">
              <InputLabel>Link-removal wp-admin</InputLabel>
              <Select
                value={local.linkRemovalWpAdminStatus ?? ""}
                label="Link-removal wp-admin"
                onChange={(event) => handleLinkRemovalWpAdminStatusChange(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box flex="1 1 140px" maxWidth={200}>
            <FormControl fullWidth size="small">
              <InputLabel>Плагин (articles)</InputLabel>
              <Select
                value={local.pluginArticleStatusFilter ?? ""}
                label="Плагин (articles)"
                onChange={(event) => handlePluginArticleStatusChange(event.target.value)}
              >
                {PLUGIN_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box flex="1 1 140px" maxWidth={200}>
            <FormControl fullWidth size="small">
              <InputLabel>Плагин (homepage)</InputLabel>
              <Select
                value={local.pluginHomepageStatusFilter ?? ""}
                label="Плагин (homepage)"
                onChange={(event) => handlePluginStatusChange(event.target.value)}
              >
                {PLUGIN_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box flex="1 1 140px" maxWidth={200}>
            <FormControl fullWidth size="small">
              <InputLabel>Плагин (sitewide)</InputLabel>
              <Select
                value={local.pluginSitewideStatusFilter ?? ""}
                label="Плагин (sitewide)"
                onChange={(event) => handlePluginSitewideStatusChange(event.target.value)}
              >
                {PLUGIN_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box flex="1 1 140px" maxWidth={200}>
            <FormControl fullWidth size="small">
              <InputLabel>Плагин (link-removal)</InputLabel>
              <Select
                value={local.pluginLinkRemovalStatusFilter ?? ""}
                label="Плагин (link-removal)"
                onChange={(event) => handlePluginLinkRemovalStatusChange(event.target.value)}
              >
                {PLUGIN_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box flex="1 1 130px" maxWidth={180}>
            <TextField fullWidth
              size="small"
              label="Создан от"
              type="date"
              value={local.dateFrom}
              onChange={(event) => updateField("dateFrom", event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
          <Box flex="1 1 130px" maxWidth={180}>
            <TextField fullWidth
              size="small"
              label="Создан до"
              type="date"
              value={local.dateTo}
              onChange={(event) => updateField("dateTo", event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Box>
          <Box flex="1 1 140px" maxWidth={200}>
            <TextField fullWidth
              size="small"
              label="Мин. публ. (статьи)"
              type="number"
              value={local.minArticlePostsCount}
              onChange={(event) => updateField("minArticlePostsCount", event.target.value)}
            />
          </Box>
          <Box flex="1 1 140px" maxWidth={200}>
            <TextField fullWidth
              size="small"
              label="Мин. публ. (homepage)"
              type="number"
              value={local.minHomepagePostsCount}
              onChange={(event) => updateField("minHomepagePostsCount", event.target.value)}
            />
          </Box>
          <Box flex="1 1 140px" maxWidth={200}>
            <TextField fullWidth
              size="small"
              label="Макс. ошиб. (статьи)"
              type="number"
              value={local.maxArticleAttemptErrors}
              onChange={(event) => updateField("maxArticleAttemptErrors", event.target.value)}
            />
          </Box>
          <Box flex="1 1 140px" maxWidth={200}>
            <TextField fullWidth
              size="small"
              label="Макс. ошиб. (homepage)"
              type="number"
              value={local.maxHomepageAttemptErrors}
              onChange={(event) => updateField("maxHomepageAttemptErrors", event.target.value)}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={local.withoutParams}
                onChange={(event) => handleCheckboxChange(event.target.checked)}
                size="small"
              />
            }
            label="Без параметров"
            sx={{ ml: 0 }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
